import { useCallback, useEffect, useMemo, useState } from 'react';

const RECENT_LIMIT = 6;

export interface HistoryScope {
  /** The signed-in subject. History is per person, not per browser. */
  subject: string | undefined;
  institutionId: string | undefined;
  /** The active context's role code, so a role switch switches the history. */
  roleCode: string | undefined;
  /**
   * The destination ids this person may currently be offered. Anything stored outside this
   * set is ignored on read — see below.
   */
  allowedIds: readonly string[];
}

/**
 * Remembers where somebody has been and what they have pinned.
 *
 * PER PERSON, PER INSTITUTION, PER ROLE — not per browser.
 *
 * The key previously carried only the institution, which was wrong in two ways that both
 * show up in a real school. A head teacher and a bursar sharing the office machine saw each
 * other's recent pages, because one browser meant one history. And a person holding two
 * roles in the same school carried their administrator recents into their teacher context,
 * where following one leads to a page that rejects them. The subject and the role code are
 * both in the key for those reasons; changing either changes the list.
 *
 * STORED IDS ARE VALIDATED ON READ, NOT TRUSTED. Even with the role in the key, a stored
 * entry can outlive the grant that justified it — a role loses a permission, a destination
 * changes audience, or somebody edits localStorage by hand. Filtering every read against
 * what the current scope actually allows means a stale favourite quietly disappears instead
 * of offering a route to a permission error. It is a cheap second line behind the key, and
 * the kind of defence that matters precisely because the first line looks sufficient.
 *
 * localStorage rather than the server: this is a convenience, and a preference that survives
 * a refresh is worth far more than one that survives a new laptop. Storing it server-side
 * would mean an API call on every navigation to record something nobody would miss.
 *
 * Every read is defensive. A malformed or hand-edited entry must not break the palette — the
 * feature is a shortcut, and a shortcut that can crash the page is a liability.
 */
export function useCommandHistory(scope: HistoryScope) {
  const { subject, institutionId, roleCode, allowedIds } = scope;

  // Every part must be present. A key missing one of them would be shared by everybody it
  // was meant to separate — the exact leak this scoping exists to prevent.
  const scopeKey =
    subject && institutionId && roleCode
      ? `${encodeURIComponent(subject)}.${encodeURIComponent(institutionId)}.${encodeURIComponent(roleCode)}`
      : null;

  const recentKey = scopeKey ? `c1.recent.${scopeKey}` : null;
  const favouriteKey = scopeKey ? `c1.favourites.${scopeKey}` : null;

  const [recent, setRecent] = useState<string[]>([]);
  const [favourites, setFavourites] = useState<string[]>([]);

  // A Set built from a stable string, so an inline array from the caller — a new value on
  // every render — cannot re-run the effect forever.
  const allowedKey = useMemo(() => [...allowedIds].sort().join(','), [allowedIds]);
  const allowed = useMemo(
    () => new Set(allowedKey ? allowedKey.split(',') : []),
    [allowedKey],
  );

  useEffect(() => {
    setRecent(readIds(recentKey, allowed));
    setFavourites(readIds(favouriteKey, allowed));
  }, [recentKey, favouriteKey, allowed]);

  const remember = useCallback(
    (id: string) => {
      // Refused rather than stored, so a destination that should not have been reachable
      // cannot be written into the history and re-offered later.
      if (!recentKey || !allowed.has(id)) return;
      setRecent((previous) => {
        // Moved to the front rather than appended, and de-duplicated: a list that grows a
        // second copy of somewhere you visit often is a list you stop reading.
        const next = [id, ...previous.filter((entry) => entry !== id)].slice(0, RECENT_LIMIT);
        writeIds(recentKey, next);
        return next;
      });
    },
    [recentKey, allowed],
  );

  const toggleFavourite = useCallback(
    (id: string) => {
      if (!favouriteKey || !allowed.has(id)) return;
      setFavourites((previous) => {
        const next = previous.includes(id)
          ? previous.filter((entry) => entry !== id)
          : [...previous, id];
        writeIds(favouriteKey, next);
        return next;
      });
    },
    [favouriteKey, allowed],
  );

  return { recent, favourites, remember, toggleFavourite };
}

function readIds(key: string | null, allowed: ReadonlySet<string>): string[] {
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is string => typeof entry === 'string' && allowed.has(entry),
    );
  } catch {
    // Corrupt or unavailable storage — private browsing disables it entirely. An empty
    // list is a correct answer; throwing here would take down the page that hosts it.
    return [];
  }
}

function writeIds(key: string, ids: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Quota exceeded, or storage disabled. The palette still works for this session.
  }
}
